<script setup lang="ts">
/**
 * InfoBanner — a tinted "icon + title + body + optional actions" callout.
 *
 * Variants drive the surface color (background, border, icon tint) via the
 * preset's semantic ramps — no per-call hex. Use this anywhere a module
 * was rolling its own `.info-banner` / `.pending-alert` / `.locked-alert`
 * chrome (operator alerts, pending notices, lock notices, etc.).
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

export type InfoBannerVariant = 'info' | 'success' | 'warning' | 'danger' | 'neutral'

interface Props {
  /** Tint colour. Drives bg, border, icon ramp. */
  variant?: InfoBannerVariant
  /** PrimeIcons name (no `pi-` prefix). Defaults to a variant-appropriate icon. */
  icon?: PiIconName
  /** Bold title above the body. Overridden by the `#title` slot. */
  title?: string
  /** Body text. Overridden by the default slot. */
  message?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'info',
})

const defaultIconForVariant: Record<InfoBannerVariant, PiIconName> = {
  info: 'info-circle',
  success: 'check-circle',
  warning: 'exclamation-triangle',
  danger: 'exclamation-circle',
  neutral: 'info-circle',
}

const resolvedIcon = computed<PiIconName>(() => props.icon ?? defaultIconForVariant[props.variant])
</script>

<template>
  <Box flex align="start" gap="3" :class="['info-banner', `info-banner--${variant}`]">
    <Box class="info-banner__icon-wrap" align="center" justify="center">
      <PiIcon :name="resolvedIcon" class="info-banner__icon" />
    </Box>
    <Box flex direction="col" gap="1" class="info-banner__body">
      <slot name="title">
        <Text v-if="title" type="body-sm" weight="semibold">{{ title }}</Text>
      </slot>
      <Text type="body-sm" color="muted" class="info-banner__text">
        <slot>{{ message }}</slot>
      </Text>
      <Box v-if="$slots.actions" class="info-banner__actions mt-2">
        <slot name="actions" />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.info-banner {
  border-radius: var(--p-border-radius-md);
  padding: 0.875rem 1rem;
  border: 1px solid transparent;
  flex-shrink: 0;
}

.info-banner__icon-wrap {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 0.8rem;
  margin-top: 1px;
}

.info-banner__icon {
  color: var(--p-primary-contrast-color);
}

/* ── Variants ──────────────────────────────────────────────────────────── */
.info-banner--info {
  background: var(--p-info-50);
  border-color: var(--p-info-200);
}
.info-banner--info .info-banner__icon-wrap {
  background: var(--p-info-500);
}

.info-banner--success {
  background: var(--p-success-50);
  border-color: var(--p-success-200);
}
.info-banner--success .info-banner__icon-wrap {
  background: var(--p-success-600);
}

.info-banner--warning {
  background: var(--p-warning-50);
  border-color: var(--p-warning-200);
}
.info-banner--warning .info-banner__icon-wrap {
  background: var(--p-warning-600);
}

.info-banner--danger {
  background: var(--p-danger-50);
  border-color: var(--p-danger-200);
}
.info-banner--danger .info-banner__icon-wrap {
  background: var(--p-danger-600);
}

.info-banner--neutral {
  background: var(--p-surface-50);
  border-color: var(--p-surface-200);
}
.info-banner--neutral .info-banner__icon-wrap {
  background: var(--p-surface-700);
}
</style>
